import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, CircularProgress,
  ToggleButton, ToggleButtonGroup, IconButton, Tooltip,
  Drawer, TextField, Button, MenuItem, Select, FormControl,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CategoryIcon from "@mui/icons-material/Category";
import SubjectIcon from "@mui/icons-material/Subject";
import { useSnackbar } from "notistack";
import { getFeedbackList, updateFeedbackStatus, toggleFeedbackRead } from "../../DAL/Feedback/Feedback";

const THEME = "#c01e2e";

const cardSx = {
  p: 3, backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)", borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const thSx = {
  color: "#6b6b6b", fontWeight: 600, fontSize: "11px",
  textTransform: "uppercase", letterSpacing: "0.4px",
  backgroundColor: "#F2EDE4", borderBottom: "1px solid rgba(26,26,26,0.09)",
};

const statusColors = {
  new: { bg: "#eef2ff", color: "#4f46e5" },
  reviewed: { bg: "#fef3c7", color: "#92400e" },
  in_progress: { bg: "#dbeafe", color: "#1d4ed8" },
  resolved: { bg: "#f0fdf4", color: "#15803d" },
  closed: { bg: "#f3f4f6", color: "#6b7280" },
};

const STATUS_OPTIONS = ["new", "reviewed", "in_progress", "resolved", "closed"];

const Stars = ({ rating, size = 15 }) => (
  <Box sx={{ display: "flex", gap: "1px" }}>
    {[1, 2, 3, 4, 5].map((s) =>
      s <= (rating || 0)
        ? <StarIcon key={s} sx={{ fontSize: size, color: THEME }} />
        : <StarBorderIcon key={s} sx={{ fontSize: size, color: "#ddd" }} />
    )}
  </Box>
);

const InfoRow = ({ icon, label, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1.25, borderBottom: "1px solid rgba(26,26,26,0.06)" }}>
    <Box sx={{ color: THEME, mt: 0.5, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
      {children}
    </Box>
  </Box>
);

// ── Detail Drawer ─────────────────────────────────────────────────────────────

function FeedbackDrawer({ item, open, onClose, onUpdate }) {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(item?.status || "new");
  const [adminNotes, setAdminNotes] = useState(item?.adminNotes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setStatus(item.status || "new");
      setAdminNotes(item.adminNotes || "");
    }
  }, [item]);

  if (!item) return null;

  const hasChanges = status !== (item.status || "new") || adminNotes !== (item.adminNotes || "");

  const handleSave = async () => {
    setSaving(true);
    const res = await updateFeedbackStatus(item._id, status, adminNotes);
    setSaving(false);
    if (res && (res.success || res.code === 200)) {
      enqueueSnackbar("Feedback updated", { variant: "success" });
      onUpdate(item._id, { status, adminNotes });
    } else {
      enqueueSnackbar(res?.message || "Failed to update", { variant: "error" });
    }
  };

  const sc = statusColors[item.status] || statusColors.new;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, backgroundColor: "#FAF7F2" } }}>
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: "#fff", borderBottom: "1px solid rgba(26,26,26,0.09)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1a1a1a" }}>Feedback Detail</Typography>
          <Typography sx={{ fontSize: "12px", color: "#aaa", mt: 0.3 }}>
            {new Date(item.submittedAt || item.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        {/* Info Section */}
        <Paper sx={{ p: 2.5, backgroundColor: "#fff", borderRadius: "12px", border: "1px solid rgba(26,26,26,0.09)", mb: 2.5 }}>
          <InfoRow icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Submitted by">
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a" }}>{item.name || "Anonymous"}</Typography>
          </InfoRow>
          <InfoRow icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Email">
            <Typography sx={{ fontSize: "13px", color: "#1a1a1a" }}>{item.email || "—"}</Typography>
          </InfoRow>
          <InfoRow icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />} label="Date">
            <Typography sx={{ fontSize: "13px", color: "#1a1a1a" }}>
              {new Date(item.submittedAt || item.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </Typography>
          </InfoRow>
          <InfoRow icon={<CategoryIcon sx={{ fontSize: 18 }} />} label="Category">
            <Chip label={(item.category || "general").replace("_", " ")} size="small"
              sx={{ fontSize: "11px", fontWeight: 600, textTransform: "capitalize" }} />
          </InfoRow>
          <InfoRow icon={<SubjectIcon sx={{ fontSize: 18 }} />} label="Subject">
            <Typography sx={{ fontSize: "13px", color: "#1a1a1a" }}>{item.subject || "—"}</Typography>
          </InfoRow>
          <InfoRow icon={<StarIcon sx={{ fontSize: 18 }} />} label="Rating">
            <Stars rating={item.rating} size={20} />
          </InfoRow>
        </Paper>

        {/* Message */}
        <Paper sx={{ p: 2.5, backgroundColor: "#fff", borderRadius: "12px", border: "1px solid rgba(26,26,26,0.09)", mb: 2.5 }}>
          <Typography sx={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.3px", mb: 1 }}>Message</Typography>
          <Typography sx={{ fontSize: "14px", color: "#1a1a1a", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {item.message || "—"}
          </Typography>
        </Paper>

        {/* Editable Section */}
        <Paper sx={{ p: 2.5, backgroundColor: "#fff", borderRadius: "12px", border: "1px solid rgba(26,26,26,0.09)" }}>
          <Typography sx={{ fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.3px", mb: 1.5 }}>Manage</Typography>

          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b6b6b", mb: 0.5 }}>Status</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}
              sx={{ borderRadius: "8px", fontSize: "13px" }}>
              {STATUS_OPTIONS.map((s) => {
                const c = statusColors[s];
                return (
                  <MenuItem key={s} value={s} sx={{ fontSize: "13px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: c.color }} />
                      {s.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b6b6b", mb: 0.5 }}>Admin Notes</Typography>
          <TextField
            fullWidth multiline rows={3} value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add internal notes about this feedback..."
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px", backgroundColor: "#fff",
                "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
                "&.Mui-focused fieldset": { borderColor: THEME, borderWidth: "1.5px" },
              },
            }}
          />

          <Button variant="contained" fullWidth disabled={!hasChanges || saving} onClick={handleSave}
            sx={{
              backgroundColor: THEME, color: "#fff", fontWeight: 600, py: 1.2, borderRadius: "10px",
              textTransform: "none", fontSize: "14px", boxShadow: "none",
              "&:hover": { backgroundColor: "#a81828", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "rgba(192,30,46,0.3)", color: "#fff" },
            }}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save Changes"}
          </Button>
        </Paper>
      </Box>
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Feedback() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200, page: 1 };
    if (statusFilter !== "all") params.status = statusFilter;

    getFeedbackList(params)
      .then((res) => {
        if (res && res.success) {
          const sorted = [...(res.data || [])].sort((a, b) => (b.rating || 0) - (a.rating || 0));
          setFeedback(sorted);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const handleToggleRead = async (e, id, currentlyRead) => {
    e.stopPropagation();
    const res = await toggleFeedbackRead(id, !currentlyRead);
    if (res && res.success) {
      setFeedback((prev) =>
        prev.map((f) => (f._id === id ? { ...f, isRead: !currentlyRead } : f))
      );
    }
  };

  const handleDrawerUpdate = (id, updates) => {
    setFeedback((prev) =>
      prev.map((f) => (f._id === id ? { ...f, ...updates } : f))
    );
    setSelectedItem((prev) => (prev && prev._id === id ? { ...prev, ...updates } : prev));
  };

  const displayed = useMemo(() => {
    const filtered = showUnreadOnly ? feedback.filter((f) => !f.isRead) : feedback;
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [feedback, showUnreadOnly, page, rowsPerPage]);

  const displayedTotal = useMemo(() => {
    return showUnreadOnly ? feedback.filter((f) => !f.isRead).length : feedback.length;
  }, [feedback, showUnreadOnly]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: THEME }} />
      </Box>
    );
  }

  const unreadCount = feedback.filter((f) => !f.isRead).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a" }}>Feedback</Typography>
        <Typography sx={{ fontSize: "13px", color: "#aaa", mt: 0.5 }}>User-submitted feedback from app & web</Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(0); } }}
          size="small"
          sx={{ "& .MuiToggleButton-root": { fontSize: "12px", fontWeight: 600, textTransform: "none", px: 2 }, "& .Mui-selected": { backgroundColor: "#c01e2e !important", color: "#fff !important" } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="new">New</ToggleButton>
          <ToggleButton value="reviewed">Reviewed</ToggleButton>
          <ToggleButton value="in_progress">In Progress</ToggleButton>
          <ToggleButton value="resolved">Resolved</ToggleButton>
          <ToggleButton value="closed">Closed</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={showUnreadOnly ? "unread" : "all"}
          exclusive
          onChange={(_, v) => { if (v) { setShowUnreadOnly(v === "unread"); setPage(0); } }}
          size="small"
          sx={{ "& .MuiToggleButton-root": { fontSize: "12px", fontWeight: 600, textTransform: "none", px: 2 }, "& .Mui-selected": { backgroundColor: "#c01e2e !important", color: "#fff !important" } }}
        >
          <ToggleButton value="unread">Unread ({unreadCount})</ToggleButton>
          <ToggleButton value="all">All ({feedback.length})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Paper sx={{ ...cardSx, p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Date</TableCell>
                <TableCell sx={thSx}>User</TableCell>
                <TableCell sx={thSx}>Rating</TableCell>
                <TableCell sx={thSx}>Category</TableCell>
                <TableCell sx={thSx}>Subject</TableCell>
                <TableCell sx={thSx}>Status</TableCell>
                <TableCell sx={{ ...thSx, textAlign: "center" }}>Read</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((item) => {
                const sc = statusColors[item.status] || statusColors.new;
                return (
                  <TableRow key={item._id} hover onClick={() => setSelectedItem(item)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#faf7f2" }, opacity: item.isRead ? 0.55 : 1 }}>
                    <TableCell sx={{ fontSize: "12px", color: "#6b6b6b", whiteSpace: "nowrap" }}>
                      {new Date(item.submittedAt || item.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a" }}>{item.name || "Anonymous"}</Typography>
                      <Typography sx={{ fontSize: "11px", color: "#aaa" }}>{item.email || ""}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stars rating={item.rating} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(item.category || "general").replace("_", " ")}
                        size="small"
                        sx={{ fontSize: "11px", fontWeight: 600, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#1a1a1a", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.subject || (item.message ? item.message.slice(0, 60) : "—")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(item.status || "new").replace("_", " ")}
                        size="small"
                        sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600, fontSize: "11px", textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title={item.isRead ? "Mark unread" : "Mark read"}>
                        <IconButton size="small" onClick={(e) => handleToggleRead(e, item._id, item.isRead)}>
                          {item.isRead
                            ? <VisibilityOffIcon sx={{ fontSize: 18, color: "#aaa" }} />
                            : <VisibilityIcon sx={{ fontSize: 18, color: THEME }} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {displayed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "#aaa" }}>
                    {showUnreadOnly ? "All caught up — no unread feedback" : "No feedback found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={displayedTotal}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: "1px solid rgba(26,26,26,0.09)" }}
        />
      </Paper>

      <FeedbackDrawer
        item={selectedItem}
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onUpdate={handleDrawerUpdate}
      />
    </Box>
  );
}
